import awsExports from '../aws-exports';

/**
 * Working DynamoDB service using the original Amplify GraphQL API
 * This API has working table structure and no input validation issues
 */
class WorkingDynamoService {
  constructor() {
    this.endpoint = awsExports.aws_appsync_graphqlEndpoint;
    this.apiKey = awsExports.aws_appsync_apiKey;
    
    // Service initialized
  }

  /**
   * Make GraphQL request to working Amplify API
   */
  async graphqlRequest(query, variables = {}) {
    try {
      console.log('📡 Making GraphQL request to working Amplify API...');

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
        },
        body: JSON.stringify({
          query,
          variables
        })
      });

      console.log('📊 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ GraphQL request failed:', response.status, errorText);
        throw new Error(`GraphQL request failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ GraphQL response received');

      if (result.errors) {
        console.error('❌ GraphQL errors:', result.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      return result;

    } catch (error) {
      console.error('❌ GraphQL request error:', error);
      throw error;
    }
  }

  /**
   * Load all domains using working Amplify schema
   */
  async loadDomains() {
    try {
      const query = `
        query ListDomains {
          listDomains {
            items {
              id
              domain
              isVerified
              verificationToken
              verificationInstructions
              redirectType
              owner
              createdAt
              updatedAt
            }
          }
        }
      `;

      const result = await this.graphqlRequest(query);
      return result.data.listDomains.items || [];

    } catch (error) {
      console.error('❌ Error loading domains:', error);
      return [];
    }
  }

  /**
   * Create domain using working Amplify schema
   */
  async createDomain(domainData, userId = 'admin-user') {
    try {
      const verificationToken = this.generateVerificationToken();
      
      const input = {
        domain: domainData.domain,
        redirectType: domainData.redirectType || 'TEMPORARY',
        owner: userId,
        isVerified: false,
        verificationToken: verificationToken,
        verificationInstructions: this.getDNSInstructions(domainData.domain, verificationToken)
      };

      console.log('🌐 Creating domain with simplified input:', input);

      const mutation = `
        mutation CreateDomain($input: CreateDomainInput!) {
          createDomain(input: $input) {
            id
            domain
            isVerified
            verificationToken
            verificationInstructions
            redirectType
            owner
            createdAt
            updatedAt
          }
        }
      `;

      const result = await this.graphqlRequest(mutation, { input });
      console.log('✅ Domain created successfully:', result.data.createDomain);
      return result.data.createDomain;

    } catch (error) {
      console.error('❌ Error creating domain:', error);
      throw error;
    }
  }

  /**
   * Update domain verification status
   */
  async updateDomain(domainId, updates) {
    try {
      const input = {
        id: domainId,
        ...updates
      };

      const mutation = `
        mutation UpdateDomain($input: UpdateDomainInput!) {
          updateDomain(input: $input) {
            id
            domain
            isVerified
            verifiedAt: updatedAt
          }
        }
      `;

      const result = await this.graphqlRequest(mutation, { input });
      return result.data.updateDomain;

    } catch (error) {
      console.error('❌ Error updating domain:', error);
      throw error;
    }
  }

  /**
   * Real domain verification with DNS checking
   */
  async verifyDomain(domain) {
    try {
      console.log('🔍 Performing real DNS verification for:', domain.domain);
      
      // Check DNS TXT record using Cloudflare DNS-over-HTTPS
      const dnsQuery = `https://cloudflare-dns.com/dns-query?name=_amplify-verification.${domain.domain}&type=TXT`;
      
      const response = await fetch(dnsQuery, {
        method: 'GET',
        headers: {
          'Accept': 'application/dns-json'
        }
      });

      if (!response.ok) {
        throw new Error(`DNS query failed: ${response.status}`);
      }

      const dnsData = await response.json();
      console.log('📋 DNS Response:', dnsData);

      let isVerified = false;
      const foundRecords = [];

      if (dnsData.Answer && dnsData.Answer.length > 0) {
        for (const record of dnsData.Answer) {
          if (record.type === 16) { // TXT record
            const txtValue = record.data.replace(/"/g, '');
            foundRecords.push(txtValue);
            
            if (txtValue === domain.verificationToken) {
              isVerified = true;
              console.log('✅ Verification token found in DNS!');
              break;
            }
          }
        }
      }

      if (isVerified) {
        // Update domain in database
        await this.updateDomain(domain.id, { 
          isVerified: true
        });
      }

      console.log('📋 Domain verification result:', { isVerified, foundRecords });

      return {
        isVerified,
        domain: domain.domain,
        token: domain.verificationToken,
        foundRecords,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('❌ Domain verification error:', error);
      return {
        isVerified: false,
        error: error.message,
        domain: domain.domain,
        token: domain.verificationToken
      };
    }
  }

  /**
   * Generate verification token
   */
  generateVerificationToken() {
    return 'amplify-verify-' + Math.random().toString(36).substr(2, 16);
  }

  /**
   * Get DNS setup instructions
   */
  getDNSInstructions(domain, token) {
    return `Add this TXT record to your DNS settings:
    
Host/Name: _amplify-verification.${domain}
Value: ${token}
TTL: 300 (or default)

After adding the record, click 'Verify Domain' to complete the setup.`;
  }
}

export default new WorkingDynamoService();